/**
 * Get Available Series from Chart
 *
 * Extracts all available series (OHLC + indicators) from chart data
 * for use as source options in dependent indicators.
 *
 * @module getAvailableSeries
 */

import { SOURCE_PATH_TYPES } from "@chartLib/lib/utils/getSourcePath";
import { isPhaseEnabled } from "../config/featureFlags.js";
import { getBaseDisplayName } from "./indicatorDisplayName.js";

/**
 * Get display name for an indicator based on its configuration
 *
 * DEPRECATED: Use getBaseDisplayName from indicatorDisplayName.js instead.
 * This function is kept for backward compatibility only.
 *
 * @param {Object} indicator - Indicator configuration
 * @returns {string} Display name for dropdown
 * @deprecated Use getBaseDisplayName from indicatorDisplayName.js
 */
function getIndicatorDisplayName(indicator) {
  return getBaseDisplayName(indicator);
}

/**
 * Get available sub-values for multi-value indicators
 * @param {string} indicatorKey - Indicator key (e.g., 'macd', 'bb', 'stochastic')
 * @returns {Array} List of sub-values with labels
 *
 * @example
 * getIndicatorSubValues('macd')
 * // Returns: [
 * //   { subKey: 'macd', label: 'MACD' },
 * //   { subKey: 'signal', label: 'Signal' },
 * //   { subKey: 'divergence', label: 'Divergence' }
 * // ]
 */
function getIndicatorSubValues(indicatorKey) {
  const subValueMap = {
    // Oscillators with multiple lines
    macd: [
      { subKey: "macd", label: "MACD" },
      { subKey: "signal", label: "Sinal" },
      { subKey: "divergence", label: "Divergência" },
    ],
    stochastic: [
      { subKey: "K", label: "%K" },
      { subKey: "D", label: "%D" },
    ],

    // Volatility bands
    bollingerband: [
      { subKey: "top", label: "Banda Superior" },
      { subKey: "middle", label: "Banda Média" },
      { subKey: "bottom", label: "Banda Inferior" },
    ],
    keltner: [
      { subKey: "top", label: "Banda Superior" },
      { subKey: "middle", label: "Banda Média" },
      { subKey: "bottom", label: "Banda Inferior" },
    ],

    // Trend indicators
    ichimoku: [
      { subKey: "tenkan", label: "Tenkan-sen" },
      { subKey: "kijun", label: "Kijun-sen" },
      { subKey: "senkouA", label: "Senkou Span A" },
      { subKey: "senkouB", label: "Senkou Span B" },
      { subKey: "chikou", label: "Chikou Span" },
    ],

    // Volume indicators
    volume: [
      { subKey: "volume", label: "Volume" },
      // Note: 'direction' is internal metadata for bar coloring, not a source value
    ],

    // Momentum
    ppo: [
      { subKey: "ppo", label: "PPO" },
      { subKey: "signal", label: "Sinal" },
      { subKey: "histogram", label: "Histograma" },
    ],
  };

  // Default for scalar indicators (single value)
  const defaultSubValue = [{ subKey: indicatorKey, label: "Valor" }];

  return subValueMap[indicatorKey] || defaultSubValue;
}

/**
 * Get all available series from chart indicators
 * @param {Array} indicators - List of chart indicators
 * @param {string} currentPanel - Current panel ID (to filter same-panel only)
 * @param {string} excludeIndicatorId - Indicator ID to exclude (when editing)
 * @returns {Array} List of available series options
 *
 * @example
 * getAvailableSeriesFromIndicators(indicators, 'panel_0')
 * // Returns: [
 * //   { value: 'uuid-1.rsi', label: 'RSI(14)', category: 'indicator', disabled: false },
 * //   { value: 'uuid-2.ema', label: 'EMA(9,CLOSE)', category: 'indicator', disabled: false }
 * // ]
 *
 * IMPORTANT: Uses IMMUTABLE indicator.id in value (not renderKey).
 * This ensures dependencies persist when indicator configs change (e.g., RSI(14) → RSI(16)).
 */
export function getAvailableSeriesFromIndicators(
  indicators = [],
  currentPanel = null,
  excludeIndicatorId = null,
) {
  if (!indicators || indicators.length === 0) {
    return [];
  }

  const uiPhaseEnabled = isPhaseEnabled("SERIES_AS_SOURCE", "UI_ONLY");

  return indicators
    .filter((indicator) => {
      // Exclude current indicator (can't depend on itself)
      if (excludeIndicatorId && indicator.id === excludeIndicatorId) {
        return false;
      }

      // TODO: Filter by panel when panel context is available
      // For now, include all indicators

      return true;
    })
    .flatMap((indicator) => {
      // Use indicator's actual displayName (includes source context from updateIndicatorDisplayName)
      // Fallback to local getIndicatorDisplayName if displayName not set
      const displayName =
        indicator.displayName || getIndicatorDisplayName(indicator);
      const subValues = getIndicatorSubValues(indicator.key);

      // CRITICAL: Use immutable ID, not mutable renderKey
      const indicatorId = indicator.id;

      // Create option for each sub-value
      return subValues.map((sub) => {
        // Check if sub.label matches indicator key (case-insensitive)
        // If it matches, omit the redundant label (e.g., "VOLUME: Volume" → "VOLUME")
        const indicatorKeyLower = indicator.key?.toLowerCase() || "";
        const subLabelLower = sub.label?.toLowerCase() || "";
        const shouldOmitLabel = indicatorKeyLower === subLabelLower;

        // Generate label
        let label;
        if (subValues.length === 1) {
          // Single value indicator: just use displayName
          label = displayName;
        } else if (shouldOmitLabel) {
          // Multi-value but label matches indicator name: omit redundant label
          label = displayName;
        } else {
          // Multi-value with distinct label: include it
          label = `${displayName}: ${sub.label}`;
        }

        return {
          value: `${indicatorId}.${sub.subKey}`, // Use ID, not renderKey!
          label,
          category: "indicator",
          indicatorKey: indicator.key,
          disabled: !uiPhaseEnabled, // Disabled until UI_ONLY phase is enabled
        };
      });
    });
}

/**
 * Get complete list of source options including OHLC and indicators
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeCalculated - Include calculated OHLC fields
 * @param {Array} options.indicators - Chart indicators
 * @param {string} options.currentPanel - Current panel ID
 * @param {string} options.excludeIndicatorId - Indicator to exclude
 * @returns {Array} Complete source options list
 *
 * @example
 * getCompleteSourceOptions({
 *   includeCalculated: true,
 *   indicators: chartIndicators,
 *   currentPanel: 'panel_0'
 * })
 * // Returns: [OHLC options, divider, indicator options]
 */
export function getCompleteSourceOptions({
  includeCalculated = true,
  indicators = [],
  currentPanel = null,
  excludeIndicatorId = null,
} = {}) {
  // Base OHLC options
  const ohlcOptions = includeCalculated
    ? SOURCE_PATH_TYPES
    : SOURCE_PATH_TYPES.filter((opt) => opt.type === "basic");

  // Get indicator series options
  const seriesOptions = getAvailableSeriesFromIndicators(
    indicators,
    currentPanel,
    excludeIndicatorId,
  );

  // Combine with divider if there are series
  if (seriesOptions.length > 0) {
    return [
      ...ohlcOptions,
      {
        label: "─────────",
        value: "__divider__",
        disabled: true,
        category: "divider",
      },
      ...seriesOptions,
    ];
  }

  return ohlcOptions;
}

// `isSeriesSource` moved to the leaf module `seriesSource.js` to break the import
// cycle with indicatorDisplayName.js. Re-exported here so existing importers
// (IndicatorDependencyBadge, PropertyTypes) stay unchanged.
export { isSeriesSource } from "./seriesSource.js";
